import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { usePrintStore } from '@renderer/stores/printStore'
import { useTranslation } from 'react-i18next'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import cogoToast from '@successtar/cogo-toast'
import Button from '@renderer/components/bahela/Button'
import QrCodeImage from '@renderer/components/QrCodeImage'
import { FaMinus, FaPlus } from 'react-icons/fa'
import MidtransUtils, { VoucherOnlyRegionError } from '@renderer/utils/midtransUtils'
import { useInterval } from '@renderer/utils/useInterval'
import { useOutletContext } from 'react-router-dom'
import PinModal from '@renderer/components/PinModal'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

// Bahela brand backdrop — same asset ConsentModal uses, so the reprint modal
// fills the screen with the themed background instead of leaking the grey app
// root through a transparent overlay.
import bgLayer from '/assets/bahela/bg-layer.webp'
import { qrActivityLogger } from '@renderer/utils/qrLoadReporter'

interface UserReprintModalProps {
  isVisible: boolean
  onComplete: () => void
  onSkip: () => void
  stripPreview?: string
  timeout?: number
}

const UserReprintModal: React.FC<UserReprintModalProps> = ({
  isVisible,
  onComplete,
  onSkip,
  stripPreview: propStripPreview,
  timeout = 60
}) => {
  const { t } = useTranslation()
  const { gallery, id, userPreferences, logActivity, setSessionState, uploadSessionState } = useSessionStateStore()
  const { config: globalConfig } = useConfigStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const { password } = useSettingsStore()
  // Outlet context retained for forward-compat consumers; reprint pricing
  // now follows the project's stamped resolvedCurrency.
  useOutletContext<{ user: { email: string } } | undefined>()
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)

  // Voucher-only gating: same contract as the default reprint modal /
  // view/Checkout / view/Payment. Operator-PIN bypass stays a separate
  // flow.
  const reprintTier = globalConfig?.paymentSetupTier
  const reprintHasCustomGateway = Boolean(globalConfig?.hasCustomGateway)
  // `global_hidden` (e.g. MXN) is voucher-only ONLY without a custom gateway;
  // a wired Mercado Pago makes online payment available. See view/Payment.tsx.
  const isVoucherOnlyRegion =
    (reprintTier === 'global_hidden' ||
      reprintTier === 'localized_in_progress') &&
    !reprintHasCustomGateway

  const [stripPreview, setStripPreview] = useState<string>(propStripPreview || '')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [showPayment, setShowPayment] = useState<boolean>(false)
  const [showPinModal, setShowPinModal] = useState<boolean>(false)
  const [showVoucherInput, setShowVoucherInput] = useState<boolean>(false)
  const [voucherError, setVoucherError] = useState<string>('')
  const [isVoucherLoading, setIsVoucherLoading] = useState<boolean>(false)
  const [qrUrl, setQrUrl] = useState<string>('')
  const [snapToken, setSnapToken] = useState<string>('')
  const [isCheckingPayment, setIsCheckingPayment] = useState<boolean>(false)
  const [isExpired, setIsExpired] = useState<boolean>(false)
  const [isOnline, setIsOnline] = useState<boolean>(true)
  const [orderId, setOrderId] = useState<string | null>(null)
  const [remainingTime, setRemainingTime] = useState<number>(timeout)

  const isMountedRef = useRef<boolean>(true)
  // The parent passes inline onSkip/onComplete and re-renders on every session-store
  // write (logActivity included). Read them through refs so a re-render never re-runs
  // the open/reset effect below — that re-run is what threw guests off the QR screen
  // back to the copies picker the instant "Reprint Payment Initiated" was logged.
  const onSkipRef = useRef(onSkip)
  onSkipRef.current = onSkip
  const logActivityRef = useRef(logActivity)
  logActivityRef.current = logActivity
  // Synchronous latch so rapid taps on "Pay & Print" can't open several gateway orders.
  const isInitiatingRef = useRef<boolean>(false)
  // Bumped on every attempt and on Cancel, so a QR response that lands after the guest
  // cancelled is dropped instead of reviving an order nobody can see.
  const paymentAttemptRef = useRef<number>(0)
  // Synchronous guards to prevent duplicate prints from async race conditions.
  // React state updates (setIsCheckingPayment) are batched and async — the polling
  // interval can fire again before the state change takes effect. Refs update instantly.
  const paymentHandledRef = useRef<boolean>(false)
  const isPrintingRef = useRef<boolean>(false)

  const packages = useMemo(
    () =>
      globalConfig?.pages?.result?.settings?.reprintPackages ||
      globalConfig?.pages?.checkout?.settings?.packages ||
      [],
    [globalConfig]
  )
  const printPackages = useMemo(
    () =>
      packages
        .filter((pkg) => pkg.type === 'print')
        .sort((a, b) => (a.quantity || 0) - (b.quantity || 0)),
    [packages]
  )

  const [selectedQuantity, setSelectedQuantity] = useState<number>(() =>
    printPackages.length > 0 ? printPackages[0].quantity : 1
  )

  const selectedPackage = useMemo(
    () => printPackages.find((pkg) => pkg.quantity === selectedQuantity),
    [printPackages, selectedQuantity]
  )
  const currentPrice = selectedPackage?.price || 0
  const paymentTimeoutConfig = globalConfig?.pages?.payment?.settings?.paymentTimeout || 120

  // Printable strip source, independent of the on-screen preview. Prefer the in-memory blob, then
  // the uploaded (R2) URL, then the prop/gallery strip. This is what actually gates + gets printed,
  // so a missing local blob (e.g. web runtime) no longer bricks "Pay & Print" when the strip did
  // upload — mirrors the default view modal's stripUrlForPrint separation, plus the R2 fallback.
  const stripUrlForPrint = useMemo(() => {
    const stripItem = gallery.capturedItems.find((item) => item.id?.includes('dreambooth-strip'))
    return (
      stripItem?.stripUrl ||
      stripItem?.stripRemoteUrl ||
      gallery.stripUrl ||
      propStripPreview ||
      ''
    )
  }, [gallery.capturedItems, gallery.stripUrl, propStripPreview])

  // Open/reset — runs only when the modal opens (or the configured timeout changes).
  // Must not depend on anything that changes while the guest is mid-payment.
  useEffect(() => {
    if (!isVisible) return

    isMountedRef.current = true
    setRemainingTime(timeout)
    setShowPayment(false)
    setIsProcessing(false)
    isInitiatingRef.current = false

    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then((status: boolean) => setIsOnline(status))
      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) =>
        setIsOnline(status)
      )
      return (): void => {
        isMountedRef.current = false
        // A reopen must not render one frame with last open's 0 and time out at once.
        setRemainingTime(timeout)
        unsubscribe()
      }
    }

    return (): void => {
      isMountedRef.current = false
      setRemainingTime(timeout)
    }
  }, [isVisible, timeout])

  // Keep the preview in step with the strip as it lands/uploads, without touching
  // payment state.
  useEffect(() => {
    if (!isVisible) return
    if (propStripPreview) {
      setStripPreview(propStripPreview)
    } else {
      const stripItem = gallery.capturedItems.find((item) =>
        item.id?.includes('dreambooth-strip')
      )
      setStripPreview(
        stripItem?.stripUrl || stripItem?.stripRemoteUrl || gallery.stripUrl || ''
      )
    }
  }, [isVisible, propStripPreview, gallery.capturedItems, gallery.stripUrl])

  // One countdown, owned by this effect: ticks only on the copies picker, paused while
  // the QR is up. Cancel resets remainingTime and flips showPayment, which restarts it.
  useEffect(() => {
    if (!isVisible || showPayment) return
    const intervalId = setInterval(() => {
      setRemainingTime((prev) => Math.max(0, prev - 1))
    }, 1000)
    return (): void => clearInterval(intervalId)
  }, [isVisible, showPayment])

  useEffect(() => {
    if (!isVisible || showPayment || remainingTime > 0) return
    logActivityRef.current('Reprint modal timed out, skipping')
    onSkipRef.current()
  }, [isVisible, showPayment, remainingTime])

  const handleQuantityChange = (direction: 'increase' | 'decrease'): void => {
    if (printPackages.length === 0) return
    const currentIndex = printPackages.findIndex((pkg) => pkg.quantity === selectedQuantity)
    if (currentIndex === -1) return
    let nextIndex = currentIndex
    if (direction === 'increase') {
      nextIndex = Math.min(printPackages.length - 1, currentIndex + 1)
    } else {
      nextIndex = Math.max(0, currentIndex - 1)
    }
    setSelectedQuantity(printPackages[nextIndex].quantity)
  }

  const handleSkip = (): void => {
    logActivity('User skipped reprint')
    onSkip()
  }

  const handlePrint = async (): Promise<void> => {
    if (isPrintingRef.current) {
      console.log(`[REPRINT] handlePrint skipped - already printing`)
      return
    }
    isPrintingRef.current = true
    paymentHandledRef.current = true
    setIsCheckingPayment(false)

    // Record + upload the paid reprint to the Session record BEFORE printing,
    // so a print failure (no printer, fetch error) can never lose the revenue
    // from the Sessions data. Mirrors the generic view/samu UserReprintModal
    // ordering — the bahela modal previously recorded only after a successful
    // print, silently dropping paid reprints when printing threw.
    const currentState = useSessionStateStore.getState()
    const currentExtraPrint = currentState.transaction.extraPrintAmount || 0
    const newExtraPrint = currentExtraPrint + currentPrice
    const currentExtraCopies = currentState.transaction.extraPrintCopies || 0
    const newExtraCopies = currentExtraCopies + selectedQuantity

    console.log(`[REPRINT] Updating extraPrintAmount: ${currentExtraPrint} + ${currentPrice} = ${newExtraPrint}`)
    console.log(`[REPRINT] Updating extraPrintCopies: ${currentExtraCopies} + ${selectedQuantity} = ${newExtraCopies}`)

    setSessionState({
      transaction: {
        extraPrintAmount: newExtraPrint,
        extraPrintCopies: newExtraCopies,
        extraPrintPaymentStatus: 'bypass'
      }
    })

    console.log(`[REPRINT] Uploading session ${id} with reprint data...`)
    try {
      await uploadSessionState(id)
      console.log(`[REPRINT] Session ${id} uploaded successfully with extraPrintAmount: ${newExtraPrint}`)
    } catch (err) {
      console.error(`[REPRINT] Failed to upload session ${id} after reprint:`, err)
    }

    if (!isPrintingEnabled) {
      cogoToast.warn('Printing is disabled')
    } else {
      try {
        const printerConfig = getPrinterForFrame(userPreferences.frameId || '')
        if (!printerConfig) throw new Error('No printer available')

        cogoToast.loading('Printing your photos...')

        const printSource = stripUrlForPrint || stripPreview
        if (!printSource) throw new Error('Strip not available to print')
        const response = await fetch(printSource)
        const blob = await response.blob()
        const reprintId = `reprint-${id || 'session'}-${Date.now()}`

        await initiatePrint(
          blob,
          printerConfig.printerName,
          selectedQuantity,
          printerConfig.settings,
          true,
          reprintId,
          'user_reprint',
          { sessionId: id || undefined, projectId: globalConfig?._id || undefined }
        )

        cogoToast.success('Printing started!')
        logActivity(`User reprinted ${selectedQuantity} copies`)
      } catch (error) {
        console.error('Print error:', error)
        cogoToast.error('Failed to print')
      }
    }

    isPrintingRef.current = false
    setIsProcessing(false)
    setTimeout(() => {
      if (isMountedRef.current) onComplete()
    }, 2000)
  }

  const handlePayment = async (): Promise<void> => {
    if (!selectedPackage) {
      cogoToast.error('Invalid selection')
      return
    }

    // Free-reprint fast path: a zero-priced package never touches the payment
    // gateway — record it and print directly. Mirrors the generic modal; the
    // bahela modal previously sent price 0 into MidtransUtils.initiatePayment.
    const grossAmount = Math.max(0, selectedPackage.price ?? 0)
    if (grossAmount <= 0) {
      logActivity('User requested free reprint', { quantity: selectedQuantity })
      setIsProcessing(true)
      await handlePrint()
      return
    }

    // Voucher-only: open the customer voucher input instead of /api/payments.
    if (isVoucherOnlyRegion) {
      logActivity('Reprint redirected to voucher entry (voucher-only region)', {
        paymentSetupTier: reprintTier
      })
      setVoucherError('')
      setShowVoucherInput(true)
      return
    }
    if (!isOnline) {
      cogoToast.error('No internet connection')
      return
    }
    if (isInitiatingRef.current) return
    isInitiatingRef.current = true
    const attempt = ++paymentAttemptRef.current

    setIsProcessing(true)
    setShowPayment(true)
    setIsExpired(false)
    setQrUrl('')
    setSnapToken('')
    paymentHandledRef.current = false
    isPrintingRef.current = false

    try {
      const data = await MidtransUtils.initiatePayment(
        selectedPackage.price,
        id || '',
        paymentTimeoutConfig,
        globalConfig?._id || '',
        userPreferences.filterId || '',
        userPreferences.frameId || '',
        0,
        true
      )

      if (attempt !== paymentAttemptRef.current || !isMountedRef.current) return

      if (data.order_id) {
        setOrderId(data.order_id)
        if (data.qr_url) {
          const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/.+/g, '$1')
          setQrUrl(sanitizedQrUrl)
          setIsCheckingPayment(true)
          logActivity('Reprint Payment Initiated (QR)', {
            orderId: data.order_id,
            amount: selectedPackage.price,
            type: data.payment_type
          })
        } else if (data.snap_token) {
          setSnapToken(data.snap_token)
          logActivity('Reprint Payment Initiated (Snap)', {
            orderId: data.order_id,
            snapToken: data.snap_token
          })
          setIsCheckingPayment(true)
        } else {
          throw new Error(`Invalid Payment Response: Type=${data.payment_type}`)
        }
      } else {
        throw new Error('Failed to generate payment order')
      }
    } catch (error) {
      if (error instanceof VoucherOnlyRegionError) {
        logActivity('Reprint switched to voucher entry (backend 409)', {
          paymentSetupTier: error.paymentSetupTier
        })
        setShowPayment(false)
        setIsProcessing(false)
        setVoucherError('')
        setShowVoucherInput(true)
        return
      }
      console.error('Payment initiation error:', error)
      const msg = error instanceof Error ? error.message : 'Unknown Error'
      cogoToast.error(`Payment Error: ${msg}`)
      setShowPayment(false)
      setIsProcessing(false)
    } finally {
      isInitiatingRef.current = false
    }
  }

  /**
   * Customer voucher entry for reprint — NOT the operator-PIN bypass.
   * Validates via `/api/promos/apply`; cash vouchers settle + print
   * immediately, discount vouchers display the residual and ask the
   * customer to settle offline with the operator.
   */
  const handleVoucherSubmit = async (code: string): Promise<void> => {
    if (!selectedPackage) {
      cogoToast.error('Invalid selection')
      return
    }
    const trimmed = (code || '').trim()
    if (!trimmed) {
      setVoucherError(t('checkout.invalidPromo', 'Invalid promo code'))
      return
    }
    setVoucherError('')
    setIsVoucherLoading(true)
    try {
      const subtotal = Math.max(0, selectedPackage.price ?? 0)
      const response = await fetch(
        `${process.env.VITE_API_URL}/api/promos/apply`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code: trimmed,
            projectId: globalConfig?._id || '',
            amount: Math.round(subtotal)
          })
        }
      )
      const data = await response.json().catch(() => ({}))
      const isValid = response.ok && data.isValid
      const isCash = Boolean(isValid && data.isCash)
      if (!isValid) {
        const msg =
          data.message || data.error || t('checkout.invalidPromo', 'Invalid promo code')
        setVoucherError(msg)
        cogoToast.error(msg)
        return
      }
      if (isCash) {
        logActivity('Reprint cash voucher applied', {
          code: trimmed,
          quantity: selectedQuantity,
          amount: subtotal
        })
        cogoToast.success(t('checkout.promoApplied', 'Promo code applied successfully!'))
        setShowVoucherInput(false)
        setIsProcessing(true)
        await handlePrint()
        return
      }
      const apiDiscount = Math.round(Number(data.discount) || 0)
      const finalAmount = Math.max(0, Math.round(subtotal - apiDiscount))
      logActivity('Reprint discount voucher applied (operator settlement)', {
        code: trimmed,
        quantity: selectedQuantity,
        discount: apiDiscount,
        finalAmount
      })
      cogoToast.info(
        t(
          'reprint.voucherDiscountedSettleOffline',
          'Voucher applied. Please settle {{amount}} with the operator to continue.',
          { amount: formatAmount(finalAmount, projectCurrency) }
        )
      )
    } catch (error) {
      console.error('Voucher validation error:', error)
      const msg = t(
        'checkout.promoValidationFailed',
        'Failed to validate promo code. Please try again.'
      )
      setVoucherError(msg)
      cogoToast.error(msg)
    } finally {
      setIsVoucherLoading(false)
    }
  }

  const openSnapPayment = (): void => {
    if (!snapToken || !window.snap) {
      cogoToast.error('Payment gateway not ready')
      return
    }
    window.snap.pay(snapToken, {
      onSuccess: async (result: unknown) => {
        logActivity('Snap Payment Success', { result })
        await handlePrint()
      },
      onPending: () => setIsCheckingPayment(true),
      onError: () => cogoToast.error('Payment failed'),
      onClose: () => {}
    })
  }

  const handleCancelPayment = (): void => {
    paymentAttemptRef.current += 1
    setShowPayment(false)
    setIsCheckingPayment(false)
    setOrderId(null)
    setIsProcessing(false)
    setSnapToken('')
    setQrUrl('')
    setIsExpired(false)
    // Back on the picker with a fresh clock; the countdown effect restarts on showPayment=false.
    setRemainingTime(timeout)
  }

  const handlePinSubmit = async (pin: string): Promise<void> => {
    if (pin === password) {
      logActivity('Reprint payment bypassed with PIN', { quantity: selectedQuantity })
      setShowPinModal(false)
      if (showPayment) handleCancelPayment()
      setIsProcessing(true)
      await handlePrint()
    } else {
      cogoToast.error(t('payment.wrongCode', 'Incorrect code. Please try again.'))
    }
  }

  useEffect(() => {
    const handleBypass = async (): Promise<void> => {
      logActivity('Reprint payment bypassed via Control Panel')
      handleCancelPayment()
      await handlePrint()
    }
    window.addEventListener('user-reprint-bypass', handleBypass)
    return (): void => window.removeEventListener('user-reprint-bypass', handleBypass)
  }, [selectedQuantity, isPrintingEnabled])

  useEffect(() => {
    const event = new CustomEvent('reprint-payment-state', { detail: { visible: showPayment } })
    window.dispatchEvent(event)
    return (): void => {
      window.dispatchEvent(new CustomEvent('reprint-payment-state', { detail: { visible: false } }))
    }
  }, [showPayment])

  useInterval(
    async () => {
      if (paymentHandledRef.current) return
      if (!orderId) return
      try {
        const paymentData = await MidtransUtils.checkPaymentStatus(orderId)
        if (paymentData.isExpired) {
          setIsExpired(true)
          setIsCheckingPayment(false)
          cogoToast.warn('Payment expired')
        } else if (paymentData.paid) {
          if (paymentHandledRef.current) return
          cogoToast.success('Payment successful!')
          logActivity('Reprint Payment Successful (Verified)', { orderId })
          await handlePrint()
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
      }
    },
    isCheckingPayment && isOnline ? 3000 : null
  )

  const isMinQuantity =
    printPackages.length > 0 && selectedQuantity === printPackages[0].quantity
  const isMaxQuantity =
    printPackages.length > 0 &&
    selectedQuantity === printPackages[printPackages.length - 1].quantity

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      {/* Textured brand backdrop + a soft scrim so the saturated red never glares. */}
      <div
        className="absolute inset-0 bg-methadone bg-cover bg-center"
        style={{ backgroundImage: `url(${bgLayer})` }}
      />
      <div className="absolute inset-0 bg-black/50" />

      <div
        className="relative z-10 flex w-full max-w-6xl h-[86vh] max-h-[90vh] overflow-hidden rounded-3xl bg-methadone bg-cover bg-center shadow-2xl ring-1 ring-uriYellow/40 font-body-bahela"
        style={{ backgroundImage: `url(${bgLayer})` }}
      >
        {/* Mute the flat red so the panel is easy on the eyes — calmer, like the result page. */}
        <div className="absolute inset-0 bg-black/30 pointer-events-none" />

        {!showPayment && (
          <div className="absolute top-6 right-6 z-20">
            <span className="text-2xl font-serif-bahela text-uriYellow/90">
              {Math.floor(remainingTime / 60)}:
              {String(remainingTime % 60).padStart(2, '0')}
            </span>
          </div>
        )}

        <div className="relative z-10 flex h-full flex-1 min-h-0">
          <div className="flex-1 min-h-0 flex justify-center items-center p-6 sm:p-8">
            {stripPreview || stripUrlForPrint ? (
              // Float the strip with a soft shadow, fully contained (no cropping, no red box).
              <img
                src={stripPreview || stripUrlForPrint}
                alt="Photo Strip"
                className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
              />
            ) : (
              <div className="h-full w-full flex flex-col items-center justify-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-uriYellow border-t-transparent" />
                <span className="font-body-bahela text-uriYellow/80 text-lg">
                  {t('reprint.preparingStrip', 'Preparing your strip…')}
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 flex flex-col justify-center p-8 sm:p-12 border-l border-uriYellow/25">
            {showPayment ? (
              <div className="flex flex-col items-center justify-center h-full">
                <h2 className="text-3xl font-bold text-uriYellow font-serif-bahela mb-6">
                  {t('reprint.scanToPay', 'Complete Payment')}
                </h2>

                <div className="bg-white p-8 rounded-2xl shadow-lg mb-6 flex flex-col items-center justify-center w-[300px] h-[300px]">
                  {isExpired ? (
                    <div className="flex flex-col items-center text-hibiscus">
                      <p className="font-bold mb-2">Payment Expired</p>
                      <button
                        onClick={handlePayment}
                        className="text-sm text-uriYellow underline font-body-bahela"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : qrUrl ? (
                    <QrCodeImage
                      qrUrl={qrUrl}
                      className="w-full h-full object-contain"
                      onEvent={qrActivityLogger(logActivity, 'bahela-reprint')}
                    />
                  ) : snapToken ? (
                    <div className="flex flex-col items-center gap-4">
                      <p className="text-methadone text-center">Secure Payment Popup Ready</p>
                      <Button
                        onClick={openSnapPayment}
                        bgColor="uriYellow"
                        textColor="methadone"
                        className="px-8 py-3"
                      >
                        Pay Now
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-2 border-uriYellow border-t-transparent" />
                    </div>
                  )}
                </div>

                <p className="text-3xl font-bold text-uriYellow mb-2">
                  {formatAmount(currentPrice, projectCurrency)}
                </p>
                <p className="text-white/90 mb-8 max-w-sm text-center">
                  {qrUrl
                    ? t('reprint.paymentInstruction', 'Scan the QR code to complete purchase')
                    : 'Click Pay Now to complete your purchase securely.'}
                </p>

                <Button
                  onClick={handleCancelPayment}
                  bgColor="hibiscus"
                  textColor="uriYellow"
                  className="w-full max-w-sm py-4 text-lg"
                >
                  {t('reprint.cancel', 'Cancel')}
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-12">
                  <h1 className="text-4xl font-bold text-uriYellow font-serif-bahela mb-4">
                    {t('reprint.wantExtras', 'Want Extra Prints?')}
                  </h1>
                  <p className="text-xl text-white/90 leading-relaxed">
                    {t('reprint.subtitle', 'Get more copies of your photo strip')}
                  </p>
                </div>

                <div className="mb-12 flex flex-col items-center">
                  <div className="flex items-center gap-8 mb-4">
                    <button
                      title="Decrease Quantity"
                      onClick={() => handleQuantityChange('decrease')}
                      disabled={isMinQuantity}
                      className="w-16 h-16 rounded-full bg-uriYellow text-methadone shadow-lg flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-95"
                    >
                      <FaMinus className="text-xl" />
                    </button>

                    <div className="flex flex-col items-center min-w-[120px]">
                      <span className="text-6xl font-bold text-uriYellow tabular-nums font-serif-bahela">
                        {selectedQuantity}
                      </span>
                      <span className="text-sm text-white/80 uppercase tracking-widest mt-1">
                        {t('reprint.quantity', 'Copies')}
                      </span>
                    </div>

                    <button
                      title="Increase Quantity"
                      onClick={() => handleQuantityChange('increase')}
                      disabled={isMaxQuantity}
                      className="w-16 h-16 rounded-full bg-uriYellow text-methadone shadow-lg flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-95"
                    >
                      <FaPlus className="text-xl" />
                    </button>
                  </div>

                  <div className="mt-2">
                    <p className="text-2xl font-bold text-uriYellow">
                      {currentPrice > 0
                        ? formatAmount(currentPrice, projectCurrency)
                        : t('reprint.free', 'Free')}
                    </p>
                  </div>
                </div>

                {/* "Use Voucher Code" CTA below carries the voucher-only intent. */}
                <div className="flex flex-col gap-4">
                  <Button
                    onClick={handlePayment}
                    disabled={!stripUrlForPrint || isProcessing || !selectedPackage}
                    bgColor="uriYellow"
                    textColor="methadone"
                    className="w-full py-4 text-lg font-semibold"
                  >
                    {!stripUrlForPrint
                      ? t('reprint.preparingStrip', 'Preparing your strip…')
                      : isVoucherOnlyRegion
                        ? t('reprint.useVoucher', 'Use Voucher Code')
                        : t('reprint.print', 'Pay & Print')}
                  </Button>
                  <Button
                    onClick={handleSkip}
                    bgColor="hibiscus"
                    textColor="uriYellow"
                    className="w-full py-4 text-lg"
                  >
                    {t('reprint.skip', 'Skip')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Operator-PIN bypass: numeric 6-digit, settings password. */}
      <PinModal
        isOpen={showPinModal}
        onClose={() => setShowPinModal(false)}
        onSubmit={handlePinSubmit}
        title={t('payment.codeEntry.title', 'Enter Access Code')}
        subtitle={t('payment.codeEntry.subtitle', 'Enter 6-digit code to bypass payment')}
      />

      {/* Customer voucher entry: alphanumeric, validated via /api/promos/apply. */}
      {showVoucherInput && (
        <OnScreenKeyboard
          onEnter={handleVoucherSubmit}
          placeholder={t(
            'checkout.voucherCodePlaceholder',
            'Type or paste your code'
          )}
          error={voucherError}
          isLoading={isVoucherLoading}
          onInputChange={() => setVoucherError('')}
          onClose={() => {
            setShowVoucherInput(false)
            setVoucherError('')
          }}
        />
      )}
    </div>
  )
}

export default UserReprintModal
